import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableSortLabel,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import ArchiveIcon from '@mui/icons-material/Archive';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import ProjectModal from './ProjectModal';
import apiService from '../services/api';

const toAbsoluteUrl = (url) => {
  if (!url) return '';
  const trimmed = String(url).trim();
  if (!trimmed) return '';
  return trimmed.startsWith('http')
    ? trimmed
    : `${apiService.baseURL || ''}${trimmed}`;
};

const formatProjectDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatStorageSize = (value) => {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let nextSize = size;
  let unitIndex = 0;
  while (nextSize >= 1024 && unitIndex < units.length - 1) {
    nextSize /= 1024;
    unitIndex += 1;
  }
  return `${nextSize.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
  },
}));

const StatCard = styled(StyledCard)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  '&.images': {
    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  },
  '&.defects': {
    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  },
  '&.reports': {
    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  },
}));

const Dashboard = ({ onNavigate }) => {
  const [projects, setProjects] = useState([]);
  const [storageUsage, setStorageUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [reportPreviewOpen, setReportPreviewOpen] = useState(false);
  const [reportPreviewProject, setReportPreviewProject] = useState(null);
  const [reportBuildings, setReportBuildings] = useState([]);
  const [selectedReportBuildingId, setSelectedReportBuildingId] = useState('all');
  const [reportSearchQuery, setReportSearchQuery] = useState('');
  const [reportItems, setReportItems] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [archivingProjectId, setArchivingProjectId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Load projects on component mount
  useEffect(() => {
    loadProjects();
    loadStorageUsage();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const projectsData = await apiService.getProjectsWithStats();
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadStorageUsage = async () => {
    try {
      const data = await apiService.getStorageUsage();
      setStorageUsage(data);
    } catch (error) {
      console.error('Error loading storage usage:', error);
      setStorageUsage(null);
    }
  };

  const handleProjectSaved = (savedProject, isEdit) => {
    if (isEdit) {
      setProjects(prev => prev.map(p => p.id === savedProject.id ? { ...p, ...savedProject } : p));
      setSuccessMessage(`Project "${savedProject.name}" updated successfully`);
      loadProjects();
    } else {
      setProjects(prev => [...prev, savedProject]);
      setSuccessMessage(`Project "${savedProject.name}" created successfully`);
      loadProjects();
      // Navigate to Building Management to create/manage buildings for the new project
      if (onNavigate) {
        onNavigate('buildings', { projectId: savedProject.id, projectName: savedProject.name });
      }
    }
  };

  const handleEditProject = async () => {
    if (selectedProject) {
      try {
        const projectToEdit = await apiService.getProject(selectedProject.id);
        setEditingProject({
          ...(projects.find(p => p.id === selectedProject.id) || {}),
          ...projectToEdit,
        });
        setShowProjectModal(true);
      } catch (error) {
        console.error('Error loading project details:', error);
        const fallbackProject = projects.find(p => p.id === selectedProject.id);
        if (fallbackProject) {
          setEditingProject(fallbackProject);
          setShowProjectModal(true);
        } else {
          setError('Could not find project details');
        }
      }
    }
    handleMenuClose();
  };

  const handleProjectClick = (project) => {
    // Navigate to Buildings list (building cards) for the selected project
    if (onNavigate) {
      onNavigate('buildings', { projectId: project.id, projectName: project.name });
    }
  };

  const handleMenuClick = (event, project) => {
    event.stopPropagation(); // Prevent row click
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const handleReportClick = () => {
    if (selectedProject && onNavigate) {
      try {
        onNavigate('reports', { projectId: selectedProject.id, projectName: selectedProject.name });
        setSuccessMessage(`Navigating to reports for ${selectedProject.name}`);
      } catch (error) {
        console.error('Error navigating to reports:', error);
        setError('Failed to navigate to reports. Please try again.');
      }
    }
    handleMenuClose();
  };

  const handleDeleteProject = async () => {
    if (selectedProject) {
      try {
        setError(null);
        await apiService.deleteProject(selectedProject.id);
        setSuccessMessage(`Project "${selectedProject.name}" deleted successfully`);
        // Refresh projects list
        loadProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
        setError(`Failed to delete project "${selectedProject.name}". Please try again.`);
      }
    }
    handleMenuClose();
  };

  const handleViewProject = () => {
    if (selectedProject && onNavigate) {
      try {
        onNavigate('editor', { projectId: selectedProject.id, projectName: selectedProject.name });
        setSuccessMessage(`Opening project "${selectedProject.name}"`);
      } catch (error) {
        console.error('Error navigating to project:', error);
        setError('Failed to open project. Please try again.');
      }
    }
    handleMenuClose();
  };

  const handleManageBuildings = () => {
    if (selectedProject && onNavigate) {
      try {
        onNavigate('buildings', { projectId: selectedProject.id, projectName: selectedProject.name });
        setSuccessMessage(`Managing buildings for "${selectedProject.name}"`);
      } catch (error) {
        console.error('Error navigating to buildings:', error);
        setError('Failed to open building management. Please try again.');
      }
    }
    handleMenuClose();
  };

  const handleArchiveProject = async () => {
    const project = selectedProject;
    handleMenuClose();
    if (!project) return;

    try {
      setError(null);
      setArchivingProjectId(project.id);
      const archive = await apiService.createProjectArchive(project.id);
      const fileCount = archive?.file_count ? ` (${archive.file_count} files)` : '';
      setSuccessMessage(`Archive created for "${project.name}"${fileCount}`);
      await loadProjects();
    } catch (error) {
      console.error('Error archiving project:', error);
      setError(`Failed to archive project "${project.name}". Please try again.`);
    } finally {
      setArchivingProjectId(null);
    }
  };

  const handleOpenArchive = () => {
    const project = selectedProject;
    handleMenuClose();
    const archiveUrl = project?.latest_archive_url;
    if (!archiveUrl) {
      setError('No archive found for this project yet.');
      return;
    }
    window.open(toAbsoluteUrl(archiveUrl), '_blank', 'noopener,noreferrer');
  };

  const dashboardProjects = useMemo(() => (
    projects.filter(project => !project.archive_exists)
  ), [projects]);

  // Calculate total stats from projects still shown on Dashboard.
  const totalImages = dashboardProjects.reduce((sum, project) => sum + (project.image_count || 0), 0);
  const totalDefects = dashboardProjects.reduce((sum, project) => sum + (project.defect_count || 0), 0);
  const totalAnnotations = dashboardProjects.reduce((sum, project) => sum + (project.annotation_count || 0), 0);
  const totalBuildings = dashboardProjects.reduce((sum, project) => sum + (project.building_count || 0), 0);
  const totalReports = dashboardProjects.reduce((sum, project) => sum + (project.report_count || 0), 0);

  const stats = [
    { title: 'Total Projects', value: dashboardProjects.length.toString(), className: '' },
    { title: 'Total Buildings', value: totalBuildings.toString(), className: '' },
    { title: 'Images Uploaded', value: totalImages.toString(), className: 'images' },
    { title: 'Total Annotations', value: totalAnnotations.toString(), subtitle: 'Across all projects', className: 'defects' },
    { title: 'Reports Generated', value: totalReports.toString(), className: 'reports' },
  ];

  const storagePercent = storageUsage?.exists ? Number(storageUsage.percent_used || 0) : 0;

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setTypeFilter('all');
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (columnId) => {
    const isCurrentAscending = sortBy === columnId && sortDirection === 'asc';
    setSortDirection(isCurrentAscending ? 'desc' : 'asc');
    setSortBy(columnId);
    setPage(0);
  };

  // Use real projects data with actual counts
  const displayProjects = useMemo(() => dashboardProjects.map(project => ({
    id: project.id,
    name: project.name,
    images: project.image_count || 0,
    elevations: project.elevation_count || 0,
    defects: project.defect_count || 0,
    annotations: project.annotation_count || 0,
    buildings: project.building_count || 0,
    recommendations: 0, // TODO: Calculate recommendations from defects
    status: project.status === 1 ? 'Active' : project.status === 2 ? 'Completed' : 'Inactive',
    statusColor: project.status === 1 ? 'success' : project.status === 2 ? 'info' : 'default',
    address: project.address_detail,
    inspector: project.facade_inspector,
    archive_exists: Boolean(project.archive_exists),
    latest_archive_url: project.latest_archive_url,
    latest_archive_filename: project.latest_archive_filename,
    latest_archive_created_at: project.latest_archive_created_at,
    latest_archive_size: project.latest_archive_size || 0,
    project_type: project.project_type || '',
    notice_reference_no: project.notice_reference_no || '',
    description: project.description || '',
    report_count: project.report_count || 0,
    created_at: project.created_at,
    project_date: formatProjectDate(project.created_at),
    created_by_name: project.created_by_name || '',
  })), [dashboardProjects]);

  const projectTypeOptions = useMemo(() => (
    Array.from(new Set(displayProjects.map(project => project.project_type).filter(Boolean))).sort()
  ), [displayProjects]);

  const compareProjectValues = (a, b, columnId) => {
    const numericColumns = new Set(['images', 'elevations', 'buildings', 'annotations', 'defects', 'report_count', 'recommendations']);
    if (columnId === 'created_at') {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      const normalizedA = Number.isNaN(dateA) ? 0 : dateA;
      const normalizedB = Number.isNaN(dateB) ? 0 : dateB;
      return normalizedA - normalizedB;
    }
    if (numericColumns.has(columnId)) {
      return Number(a[columnId] || 0) - Number(b[columnId] || 0);
    }
    return String(a[columnId] || '').localeCompare(String(b[columnId] || ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  const filteredProjects = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return displayProjects.filter((project) => {
      const matchesType = typeFilter === 'all' || project.project_type === typeFilter;
      const matchesSearch = !query || [
        project.name,
        project.address,
        project.inspector,
        project.status,
        project.project_type,
        project.notice_reference_no,
        project.description,
        project.project_date,
        project.created_by_name,
      ].some(value => String(value || '').toLowerCase().includes(query));

      return matchesType && matchesSearch;
    }).sort((a, b) => {
      const result = compareProjectValues(a, b, sortBy);
      if (result !== 0) return sortDirection === 'asc' ? result : -result;
      return (b.id || 0) - (a.id || 0);
    });
  }, [displayProjects, searchQuery, typeFilter, sortBy, sortDirection]);

  const tableColumns = [
    { id: 'name', label: 'PROJECT NAME' },
    { id: 'created_at', label: 'PROJECT DATE' },
    { id: 'created_by_name', label: 'CREATED BY' },
    { id: 'images', label: 'IMAGES' },
    { id: 'elevations', label: 'ELEVATIONS' },
    { id: 'buildings', label: 'BUILDINGS' },
    { id: 'annotations', label: 'ANNOTATIONS' },
    { id: 'defects', label: 'DEFECTS' },
    { id: 'report_count', label: 'REPORTS' },
    { id: 'recommendations', label: 'RECOMMENDATIONS' },
    { id: 'status', label: 'REPORT STATUS' },
  ];

  const paginatedProjects = useMemo(() => (
    filteredProjects.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  ), [filteredProjects, page, rowsPerPage]);

  const hasActiveFilters = Boolean(searchQuery.trim()) || typeFilter !== 'all';

  const selectedReportBuilding = useMemo(() => (
    reportBuildings.find(building => String(building.id) === String(selectedReportBuildingId)) || null
  ), [reportBuildings, selectedReportBuildingId]);

  const reportScopeStats = useMemo(() => {
    if (selectedReportBuilding) {
      return selectedReportBuilding.counts || { images: 0, elevations: 0, defects: 0, reports: 0 };
    }
    return reportBuildings.reduce((acc, building) => {
      const counts = building.counts || {};
      acc.images += counts.images || 0;
      acc.elevations += counts.elevations || 0;
      acc.defects += counts.defects || 0;
      acc.reports += counts.reports || 0;
      return acc;
    }, { images: 0, elevations: 0, defects: 0, reports: 0 });
  }, [reportBuildings, selectedReportBuilding]);

  const filteredReportItems = useMemo(() => {
    const selectedId = String(selectedReportBuildingId || 'all');
    const query = reportSearchQuery.trim().toLowerCase();
    return reportItems.filter((item) => {
      const matchesBuilding = selectedId === 'all' || String(item.buildingId) === selectedId;
      const matchesSearch = !query || [
        item.filename,
        item.buildingName,
        item.created_at,
      ].some(value => String(value || '').toLowerCase().includes(query));
      return matchesBuilding && matchesSearch;
    });
  }, [reportItems, selectedReportBuildingId, reportSearchQuery]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredProjects.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredProjects.length, page, rowsPerPage]);

  const handlePreviewReports = async (project) => {
    try {
      setReportPreviewProject(project);
      setReportBuildings([]);
      setReportItems([]);
      setSelectedReportBuildingId('all');
      setReportSearchQuery('');
      setReportPreviewOpen(true);
      setReportLoading(true);
      const buildings = await apiService.getProjectBuildings(project.id);
      const entries = [];
      const buildingSummaries = [];
      for (const b of buildings || []) {
        try {
          const [reportRes, imageRes] = await Promise.all([
            apiService.getBuildingReports(b.id),
            apiService.getBuildingImages(b.id).catch(() => []),
          ]);
          const folderStructure = imageRes?.folder_structure || {};
          const images = Object.values(folderStructure).flatMap(list => Array.isArray(list) ? list : []);
          const elevationNames = new Set(images.map(img => img.elevation_name).filter(Boolean));
          const defectCount = images.reduce((sum, img) => sum + ((img.defects || []).length), 0);
          const reports = (reportRes && reportRes.reports) || [];
          buildingSummaries.push({
            ...b,
            counts: {
              images: images.length,
              elevations: elevationNames.size,
              defects: defectCount,
              reports: reports.length,
            },
          });
          reports.forEach((r) => entries.push({
            buildingId: b.id,
            buildingName: b.name,
            file_url: r.file_url,
            absolute_url: toAbsoluteUrl(r.file_url),
            filename: r.filename,
            created_at: r.created_at
          }));
        } catch (e) {
          buildingSummaries.push({
            ...b,
            counts: { images: 0, elevations: 0, defects: 0, reports: 0 },
          });
        }
      }
      setReportBuildings(buildingSummaries);
      setReportItems(entries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (e) {
      setReportBuildings([]);
      setReportItems([]);
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: '#f8f9fa',
        minHeight: '100vh',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5} flexShrink={0}>
        <Typography variant="h4" fontWeight="bold" color="#333">
          Dashboard
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingProject(null);
            setShowProjectModal(true);
          }}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1,
          }}
        >
          Add Project
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2.5} mb={3} flexShrink={0}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={2.4} key={index}>
            <StatCard className={stat.className}>
              <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle2" fontWeight="600" sx={{ opacity: 0.95 }} mb={0.75}>
                  {stat.title}
                </Typography>
                <Typography variant="h3" fontWeight="bold" lineHeight={1.1}>
                  {stat.value}
                </Typography>
                {stat.subtitle && (
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {stat.subtitle}
                  </Typography>
                )}
                {(stat.title === 'Reports Generated' || typeof stat.progress === 'number') && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, typeof stat.progress === 'number'
                      ? stat.progress
                      : (totalReports / Math.max(totalBuildings, 1)) * 100)}
                    sx={{
                      mt: 1,
                      height: 6,
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: 'white'
                      }
                    }}
                  />
                )}
              </CardContent>
            </StatCard>
          </Grid>
        ))}
      </Grid>

      <StyledCard sx={{ mb: 2.5, flexShrink: 0 }}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Box
            display="flex"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            gap={1.5}
            flexDirection={{ xs: 'column', md: 'row' }}
          >
            <Box display="flex" alignItems="center" gap={1.25} sx={{ minWidth: 180 }}>
              <CloudQueueIcon sx={{ fontSize: 26, color: '#202124' }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={600} color="#202124" lineHeight={1.15}>
                  Storage
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {storageUsage?.exists
                    ? `${formatStorageSize(storageUsage.used)} of ${formatStorageSize(storageUsage.total)} used`
                    : 'Storage usage unavailable'}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1.25} sx={{ width: { xs: '100%', md: 420 } }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, storagePercent)}
                sx={{
                  flex: 1,
                  height: 7,
                  borderRadius: 999,
                  backgroundColor: '#dadce0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    backgroundColor: '#1a73e8',
                  },
                }}
              />
              <Typography variant="body2" fontWeight={700} color="#1a73e8" sx={{ minWidth: 44, textAlign: 'right' }}>
                {storageUsage?.exists ? `${storagePercent}%` : '-'}
              </Typography>
            </Box>
            {storageUsage?.exists && (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {formatStorageSize(storageUsage.free)} available
              </Typography>
            )}
          </Box>
        </CardContent>
      </StyledCard>

      {/* Projects Overview */}
      <StyledCard sx={{ flex: 1, minHeight: 520, display: 'flex', flexDirection: 'column' }}>
        <CardContent
          sx={{
            p: 2.5,
            pb: '18px !important',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            flexDirection={{ xs: 'column', md: 'row' }}
            gap={2}
            mb={2.5}
            flexShrink={0}
          >
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Projects Overview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredProjects.length} of {displayProjects.length} projects
              </Typography>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <TextField
                size="small"
                placeholder="Search projects"
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{ minWidth: { xs: '100%', sm: 260 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 190 } }}>
                <InputLabel id="project-type-filter-label">Type</InputLabel>
                <Select
                  labelId="project-type-filter-label"
                  label="Type"
                  value={typeFilter}
                  onChange={handleTypeFilterChange}
                >
                  <MenuItem value="all">All types</MenuItem>
                  {projectTypeOptions.map(projectType => (
                    <MenuItem key={projectType} value={projectType}>
                      {projectType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                title="Clear search and filters"
                sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
          
          <TableContainer sx={{ flex: 1, minHeight: 360, overflow: 'auto' }}>
            <Table stickyHeader sx={{ tableLayout: 'auto' }}>
              <TableHead>
                <TableRow>
                  {tableColumns.map((column) => (
                    <TableCell key={column.id} sx={{ fontWeight: 'bold', py: 1.5, fontSize: 13, whiteSpace: 'nowrap' }}>
                      <TableSortLabel
                        active={sortBy === column.id}
                        direction={sortBy === column.id ? sortDirection : 'asc'}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={12}>
                      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <CircularProgress size={28} />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && paginatedProjects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12}>
                      <Box py={4} textAlign="center">
                        <Typography variant="body2" color="text.secondary">
                          {hasActiveFilters ? 'No projects match your search or filters.' : 'No projects found.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && paginatedProjects.map((project, index) => (
                  <TableRow 
                    key={project.id || index} 
                    hover
                    onClick={() => handleProjectClick(project)}
                    sx={{ 
                      cursor: 'pointer',
                      '& .MuiTableCell-root': {
                        py: 1.25,
                        fontSize: 14,
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      }
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight="500" fontSize={14}>{project.name}</Typography>
                    </TableCell>
                    <TableCell>{project.project_date}</TableCell>
                    <TableCell>{project.created_by_name || '—'}</TableCell>
                    <TableCell>{project.images}</TableCell>
                    <TableCell>{project.elevations}</TableCell>
                    <TableCell>{project.buildings}</TableCell>
                    <TableCell>{project.annotations}</TableCell>
                    <TableCell>{project.defects}</TableCell>
                    <TableCell>{project.report_count}</TableCell>
                    <TableCell>{project.recommendations}</TableCell>
                    <TableCell>
                      <Chip
                        label={project.status}
                        color={project.statusColor}
                        size="small"
                        sx={{ fontWeight: 500, height: 26, '& .MuiChip-label': { px: 1.25 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small"
                        onClick={(event) => handleMenuClick(event, project)}
                        disabled={archivingProjectId === project.id}
                      >
                        {archivingProjectId === project.id ? <CircularProgress size={18} /> : <MoreVertIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredProjects.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              flexShrink: 0,
              minHeight: 52,
              '& .MuiTablePagination-toolbar': {
                minHeight: 52,
                px: 1.5,
              },
            }}
          />
        </CardContent>
      </StyledCard>

      {/* Project Modal */}
      <ProjectModal
        open={showProjectModal}
        onClose={() => {
          setShowProjectModal(false);
          setEditingProject(null);
        }}
        onProjectCreated={handleProjectSaved}
        projectToEdit={editingProject}
      />

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleReportClick}>
          <ListItemIcon>
            <AssessmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Report</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { const p = selectedProject; handleMenuClose(); p && handlePreviewReports(p); }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Reports</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleManageBuildings}>
          <ListItemIcon>
            <BusinessIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Buildings</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleArchiveProject} disabled={Boolean(selectedProject && archivingProjectId === selectedProject.id)}>
          <ListItemIcon>
            <ArchiveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Archive Project</ListItemText>
        </MenuItem>
        {selectedProject?.archive_exists && selectedProject?.latest_archive_url && (
          <MenuItem onClick={handleOpenArchive}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open Archive</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleEditProject}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Project</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteProject}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Project</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewProject}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Project</ListItemText>
        </MenuItem>
      </Menu>

      {/* Error Snackbar */}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {reportPreviewOpen && (
        <Paper sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }} onClick={() => setReportPreviewOpen(false)}>
          <Paper onClick={(e) => e.stopPropagation()} sx={{ width: 820, maxWidth: 'calc(100vw - 32px)', maxHeight: 620, overflow: 'auto', p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} gap={2}>
              <Box>
                <Typography variant="h6">Reports for {reportPreviewProject?.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedReportBuilding ? selectedReportBuilding.name : 'All buildings'}
                </Typography>
              </Box>
              <Button onClick={() => setReportPreviewOpen(false)}>Close</Button>
            </Box>
            {reportLoading ? (
              <Box display="flex" alignItems="center" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : (
              <Box>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} mb={1.5}>
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 260 } }}>
                    <InputLabel id="report-building-filter-label">Building</InputLabel>
                    <Select
                      labelId="report-building-filter-label"
                      label="Building"
                      value={selectedReportBuildingId}
                      onChange={(event) => setSelectedReportBuildingId(event.target.value)}
                    >
                      <MenuItem value="all">All buildings</MenuItem>
                      {reportBuildings.map((building) => (
                        <MenuItem key={building.id} value={String(building.id)}>
                          {building.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    placeholder="Search reports or buildings"
                    value={reportSearchQuery}
                    onChange={(event) => setReportSearchQuery(event.target.value)}
                    sx={{ flex: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (reportPreviewProject && onNavigate) {
                        setReportPreviewOpen(false);
                        onNavigate('reports', {
                          projectId: reportPreviewProject.id,
                          projectName: reportPreviewProject.name,
                          buildingId: selectedReportBuilding?.id || null,
                          buildingName: selectedReportBuilding?.name || '',
                        });
                      }
                    }}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    Open Reports Menu
                  </Button>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
                  <Chip size="small" label={`Images ${reportScopeStats.images}`} />
                  <Chip size="small" label={`Elevations ${reportScopeStats.elevations}`} />
                  <Chip size="small" label={`Defects ${reportScopeStats.defects}`} />
                  <Chip size="small" color="primary" variant="outlined" label={`Reports ${reportScopeStats.reports}`} />
                </Stack>

                {filteredReportItems.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {reportItems.length === 0 ? 'No reports found.' : 'No reports match the selected building or search.'}
                  </Typography>
                ) : (
                  filteredReportItems.map((item, idx) => (
                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #eee' }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{item.filename}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Building: {item.buildingName}{item.created_at ? ` - ${formatProjectDate(item.created_at)}` : ''}
                        </Typography>
                      </Box>
                      <Box>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            const targetUrl = item.absolute_url || toAbsoluteUrl(item.file_url);
                            if (targetUrl) {
                              window.open(targetUrl, '_blank', 'noopener,noreferrer');
                            }
                          }}
                        >
                          Preview
                        </Button>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            )}
          </Paper>
        </Paper>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
